import React, { useEffect, useMemo } from "react";
import {
    Alert,
    Card,
    Radio,
    Select,
    Space,
    Table,
    Tag,
    Typography,
} from "antd";
import type { ColumnsType } from "antd/es/table";
import { useIntl } from "react-intl";
import {
    ColumnMappingTarget,
    ImportColumnMapping,
    ImportConflictPolicy,
    ImportCoreSlot,
    ImportMatchStrategy,
    ISegmentationField,
    SegmentationFieldType,
} from "../../services/nestjs-base-api";

const { Text } = Typography;

interface Props {
    headers: string[];
    fields: ISegmentationField[];
    mapping: ImportColumnMapping;
    matchStrategy: ImportMatchStrategy;
    conflictPolicy: ImportConflictPolicy;
    onMappingChange: (m: ImportColumnMapping) => void;
    onMatchStrategyChange: (s: ImportMatchStrategy) => void;
    onConflictPolicyChange: (p: ImportConflictPolicy) => void;
}

const CORE_SLOTS: ImportCoreSlot[] = [
    "email",
    "external_id",
    "first_name",
    "last_name",
    "company",
];

const fieldTypeColors: Record<SegmentationFieldType, string> = {
    text: "blue",
    select: "green",
    number: "purple",
    date: "gold",
    boolean: "cyan",
    multi_select: "magenta",
    currency: "orange",
};

// Cheap header similarity — exact match (case/punctuation-insensitive) wins,
// otherwise we look for a substring match in either direction.
const normalize = (s: string) => s.toLowerCase().replace(/[^a-z0-9]/g, "");
const guessTarget = (
    header: string,
    fields: ISegmentationField[],
): ColumnMappingTarget | null => {
    const h = normalize(header);
    if (!h) return null;

    const coreAliases: Record<ImportCoreSlot, string[]> = {
        email: ["email", "emailaddress", "mail"],
        external_id: ["externalid", "customerid", "id", "userid", "memberid"],
        first_name: ["firstname", "givenname", "fname"],
        last_name: ["lastname", "surname", "familyname", "lname"],
        company: ["company", "companyname", "organization", "organisation", "account"],
    };
    for (const slot of CORE_SLOTS) {
        if (coreAliases[slot].some((a) => normalize(a) === h)) {
            return { kind: "core", slot };
        }
    }
    for (const f of fields) {
        const fn = normalize(f.fieldName);
        const fl = normalize(f.fieldLabel);
        if (fn === h || fl === h) return { kind: "field", fieldName: f.fieldName };
    }
    for (const slot of CORE_SLOTS) {
        if (coreAliases[slot].some((a) => h.includes(normalize(a)))) {
            return { kind: "core", slot };
        }
    }
    for (const f of fields) {
        if (h.includes(normalize(f.fieldName)) || h.includes(normalize(f.fieldLabel))) {
            return { kind: "field", fieldName: f.fieldName };
        }
    }
    return null;
};

const targetToOptionValue = (target: ColumnMappingTarget | undefined): string => {
    if (!target || target.kind === "ignore") return "ignore";
    if (target.kind === "core") return `core:${target.slot}`;
    return `field:${target.fieldName}`;
};

const optionValueToTarget = (val: string): ColumnMappingTarget => {
    if (val === "ignore") return { kind: "ignore" };
    if (val.startsWith("core:")) {
        const slot = val.slice("core:".length) as ImportCoreSlot;
        return { kind: "core", slot };
    }
    if (val.startsWith("field:")) {
        return { kind: "field", fieldName: val.slice("field:".length) };
    }
    return { kind: "ignore" };
};

interface Row {
    header: string;
    target: ColumnMappingTarget;
}

export const ColumnMappingWizard: React.FC<Props> = ({
    headers,
    fields,
    mapping,
    matchStrategy,
    conflictPolicy,
    onMappingChange,
    onMatchStrategyChange,
    onConflictPolicyChange,
}) => {
    const intl = useIntl();

    // First-render auto-suggest: only fill blanks; never overwrite an explicit
    // user choice.
    useEffect(() => {
        if (headers.length === 0) return;
        let updated: ImportColumnMapping | null = null;
        for (const h of headers) {
            if (mapping[h]) continue;
            const guess = guessTarget(h, fields);
            if (guess) {
                if (!updated) updated = { ...mapping };
                updated[h] = guess;
            } else if (!updated) {
                updated = { ...mapping };
                updated[h] = { kind: "ignore" };
            } else {
                updated[h] = { kind: "ignore" };
            }
        }
        if (updated) onMappingChange(updated);
    }, [headers, fields]); // eslint-disable-line react-hooks/exhaustive-deps

    const rows = useMemo<Row[]>(() =>
        headers.map((h) => ({
            header: h,
            target: mapping[h] ?? { kind: "ignore" },
        })),
        [headers, mapping],
    );

    const handleChange = (header: string, value: string) => {
        const next = { ...mapping, [header]: optionValueToTarget(value) };
        onMappingChange(next);
    };

    const fieldOptions = fields.map((f) => ({
        value: `field:${f.fieldName}`,
        label: (
            <Space>
                <span>{f.fieldLabel}</span>
                <Tag color={fieldTypeColors[f.fieldType]}>{f.fieldType}</Tag>
                {f.fieldMetadata?.isPii && <Tag color="red">PII</Tag>}
            </Space>
        ),
    }));

    const coreSlotLabels: Record<ImportCoreSlot, { id: string; defaultMessage: string }> = {
        email: { id: "customers.import.coreSlot.email.label", defaultMessage: "Email" },
        external_id: { id: "customers.import.coreSlot.external_id.label", defaultMessage: "External ID" },
        first_name: { id: "customers.import.coreSlot.first_name.label", defaultMessage: "First name" },
        last_name: { id: "customers.import.coreSlot.last_name.label", defaultMessage: "Last name" },
        company: { id: "customers.import.coreSlot.company.label", defaultMessage: "Company" },
    };

    const coreOptions = CORE_SLOTS.map((slot) => ({
        value: `core:${slot}`,
        label: (
            <Space>
                <span>{intl.formatMessage(coreSlotLabels[slot])}</span>
                <Tag color="default">{intl.formatMessage({
                    id: "customers.import.mapping.coreTag",
                    defaultMessage: "core",
                })}</Tag>
            </Space>
        ),
    }));

    const allOptions = [
        { value: "ignore", label: <Text type="secondary">{intl.formatMessage({ id: "customers.import.mapping.ignore", defaultMessage: "(ignore this column)" })}</Text> },
        ...coreOptions,
        ...fieldOptions,
    ];

    const columns: ColumnsType<Row> = [
        {
            title: intl.formatMessage({ id: "customers.import.mapping.column.source", defaultMessage: "Source column" }),
            dataIndex: "header",
            key: "header",
            render: (h: string) => <Text strong>{h}</Text>,
        },
        {
            title: intl.formatMessage({ id: "customers.import.mapping.column.target", defaultMessage: "Maps to" }),
            key: "target",
            render: (_: unknown, row: Row) => (
                <Select
                    style={{ width: 360 }}
                    value={targetToOptionValue(row.target)}
                    options={allOptions}
                    onChange={(v) => handleChange(row.header, v)}
                />
            ),
        },
    ];

    const mappedCoreSlots = new Set(
        Object.values(mapping)
            .filter((t): t is { kind: "core"; slot: ImportCoreSlot } => t.kind === "core")
            .map((t) => t.slot),
    );
    const missingCore: string[] = [];
    if (matchStrategy === "email" && !mappedCoreSlots.has("email")) {
        missingCore.push("email");
    }
    if (matchStrategy === "external_id" && !mappedCoreSlots.has("external_id")) {
        missingCore.push("external_id");
    }

    return (
        <>
            <Card
                size="small"
                title={intl.formatMessage({ id: "customers.import.mapping.matchTitle", defaultMessage: "How should we match incoming rows to existing customers?" })}
                style={{ marginBottom: 16 }}
            >
                <Radio.Group
                    value={matchStrategy}
                    onChange={(e) => onMatchStrategyChange(e.target.value)}
                >
                    <Space direction="vertical">
                        <Radio value="email">
                            {intl.formatMessage({
                                id: "customers.import.matchStrategy.email",
                                defaultMessage: "By email (recommended)",
                            })}
                        </Radio>
                        <Radio value="external_id">
                            {intl.formatMessage({
                                id: "customers.import.matchStrategy.externalId",
                                defaultMessage: "By external ID",
                            })}
                        </Radio>
                    </Space>
                </Radio.Group>
            </Card>

            <Card
                size="small"
                title={intl.formatMessage({ id: "customers.import.mapping.conflictTitle", defaultMessage: "When a customer already exists, what should we do?" })}
                style={{ marginBottom: 16 }}
            >
                <Radio.Group
                    value={conflictPolicy}
                    onChange={(e) => onConflictPolicyChange(e.target.value)}
                >
                    <Space direction="vertical">
                        <Radio value="skip">
                            <Text>{intl.formatMessage({ id: "customers.import.conflictPolicy.skip", defaultMessage: "Skip — leave the existing record alone" })}</Text>
                        </Radio>
                        <Radio value="merge">
                            <Text>{intl.formatMessage({ id: "customers.import.conflictPolicy.merge", defaultMessage: "Merge — fill missing fields only" })}</Text>
                        </Radio>
                        <Radio value="overwrite">
                            <Text>{intl.formatMessage({ id: "customers.import.conflictPolicy.overwrite", defaultMessage: "Overwrite — replace mapped fields" })}</Text>
                        </Radio>
                    </Space>
                </Radio.Group>
            </Card>

            {missingCore.length > 0 && (
                <Alert
                    type="warning"
                    showIcon
                    style={{ marginBottom: 16 }}
                    message={intl.formatMessage(
                        { id: "customers.import.mapping.missingCore", defaultMessage: "Required column missing: {slots}" },
                        { slots: missingCore.join(", ") },
                    )}
                />
            )}

            <Table<Row>
                rowKey="header"
                size="small"
                columns={columns}
                dataSource={rows}
                pagination={false}
            />
        </>
    );
};
